import { z } from 'zod';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { MASTER_LIFECYCLE_STATUSES, archivableFieldsSchema, showLifecycleSchema } from './lifecycle.schema';
import { rankSummarySchema } from './rank.schema';

/**
 * The post type master (PA_REQUIREMENTS FR-5.1.3, §4.2).
 *
 * A post type is a BUCKET OF ACCEPTABLE RANKS — "SI / Sgt. / Subedar" is one post that
 * three peer ranks at seniority 80 can fill. A policy version is an ordered SELECTION
 * from this master; the master itself belongs to no policy and no org node.
 *
 * ⚠️ THERE IS NO GENDER FIELD HERE, AND THERE MUST NEVER BE ONE.
 *
 * "Women Police" is a post type whose eligible rank is the Women Police RANK — a
 * distinct cadre, resolved as OD-10 in P1b, seeded as one unified row in
 * `api/src/seed/rank.seed.ts`. PA_REQUIREMENTS §4.2 sketches a `requiresFemale` flag on
 * this entity; it is deliberately not built, because the eligibility it would express is
 * already expressed by `eligibleRankIds`, and a second mechanism for the same fact is
 * how the two drift. If you find yourself adding a gender field, the model is wrong.
 *
 * `isRankless` from that same sketch is also absent: nothing consumes it before P4's
 * eligibility filter, and a boolean is additive. Both are recorded as deviations in the
 * P2 CHANGELOG entry rather than left to look like oversights.
 */
export const postTypeBaseSchema = z.object({
  name: z.string().trim().min(1, 'Post type name is required').max(120),
  shortName: z.string().trim().min(1, 'Short name is required').max(40),
  /**
   * ⚠️ MULTIPLE RANKS IS THE NORMAL CASE, NOT THE EXCEPTION. Ordered, because the order
   * an administrator chose is the order the editor shows back to them.
   */
  eligibleRankIds: z
    .array(objectIdSchema)
    .min(1, 'A post type needs at least one eligible rank')
    .max(20)
    .refine((ids) => new Set(ids).size === ids.length, 'A rank may appear only once'),
  displayOrder: z.number().int().min(0).max(10_000),
});

/**
 * Defaults live HERE and never on the base schema — see `updatePostTypeSchema` below and
 * CLAUDE.md §6.
 */
export const createPostTypeSchema = postTypeBaseSchema.extend({
  displayOrder: postTypeBaseSchema.shape.displayOrder.default(0),
});

/**
 * ⚠️ `.partial()` on a base schema that carries a `.default()` produces a schema whose
 * "at least one field" refine can never fire, because the default always populates a key
 * (zod 4: `.partial()` yields `ZodOptional<ZodDefault<…>>`). `packages/shared/src/schema/
 * updateSchema.test.ts` asserts this for every update schema in the package.
 */
export const updatePostTypeSchema = postTypeBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

/** Embedded in a policy version so the builder and the grid need no second round trip. */
export const postTypeSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  shortName: z.string(),
  /**
   * ⚠️ [FIX-1] THE FACT A CONSUMER NEEDS TO STOP OFFERING AN ARCHIVED ROW.
   *
   * A published policy version stores its columns FOR EVER (P2 froze them deliberately —
   * an order raised last month must still render exactly as it was issued), so the array
   * is the RECORD and never changes when a master row is archived. What has to change is
   * what the record is USED for, and that decision cannot be made without knowing whether
   * the row is still in circulation. Carrying it on the summary is what lets ONE
   * resolution function answer it — see `web/src/lib/policyColumns.ts`.
   *
   * ⚠️ [LIFECYCLE] THIS WAS `isActive`, AND IT IS THE SAME FACT UNDER ONE VOCABULARY.
   * The summary is RESOLVED at read time and never stored, so the rename carries no data
   * implication — but it is the field `policyColumns.ts` reads, and inverting the sense
   * of the predicate there is the one edit in that file that can go silently wrong.
   */
  archivedAt: z.iso.datetime().nullable(),
});

export const postTypeSchema = postTypeBaseSchema.extend({
  id: objectIdSchema,
  /** Resolved from `eligibleRankIds`, IN THE STORED ORDER. */
  eligibleRanks: z.array(rankSummarySchema),
  /**
   * ⚠️ ARCHIVE, NEVER DELETE. Published policy versions reference post types for ever,
   * and a printed order from last year must still render. Archiving removes a row from
   * NEW policy versions only — see `policy.service.ts`'s master validation — and strips
   * the column from DRAFT arrangements, which is the whole of the cascade.
   */
  ...archivableFieldsSchema.shape,
  /**
   * `PUBLISHED` when this row has ever been part of a published policy version, derived
   * once at archive time from that monotonic fact. `null` while the row is live. It is
   * what `purgePlan.util.ts` keys on, and it is why a post type nobody ever published
   * with can eventually be purged while one that shipped an order cannot.
   */
  archivedFromStatus: z.enum(MASTER_LIFECYCLE_STATUSES).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Every value here has an index on the model (CLAUDE.md §7). `shortName` and `createdAt`
 * are absent for the reason `RANK_SORT_FIELDS` gives: an index on a collection with a
 * dozen rows, to serve a sort nobody performs.
 */
export const POST_TYPE_SORT_FIELDS = ['displayOrder', 'name'] as const;
export type PostTypeSortField = (typeof POST_TYPE_SORT_FIELDS)[number];

export const listPostTypesQuerySchema = createPaginationSchema(POST_TYPE_SORT_FIELDS, 'displayOrder')
  .extend({
    search: z.string().trim().min(1).max(120).optional(),
    /**
     * ⚠️ [LIFECYCLE] THIS REPLACED `includeInactive`, AND IT IS NOT A RENAME.
     *
     * `includeInactive=true` WIDENED the list to live-and-retired; `show` SWITCHES it,
     * and its third value is the one that was missing — an operator who archives a row
     * had no way to see it again, so the Activate action sat on a row nobody could reach.
     * See `lifecycle.schema.ts`.
     */
    show: showLifecycleSchema,
  })
  // The picker wants the administrator's own order; a `desc` default inherited from the
  // base pagination schema would show the column set backwards.
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });
